import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SEO from '../components/SEO'

function Datenschutz() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Generate WebPage Schema.org structured data for Privacy Policy
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('datenschutz.title'),
    description: t('seo.datenschutz.description'),
    url: window.location.href,
    inLanguage: t('common.back') === 'Zurück' ? 'de-DE' : 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: t('seo.siteName'),
      url: window.location.origin,
    },
    about: {
      '@type': 'Thing',
      name: 'Privacy Policy',
      description: 'GDPR compliant privacy policy for Planning Poker application',
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
  }

  return (
    <>
      <SEO page="datenschutz" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        {/* Controls - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <DarkModeToggle />
        <LanguageSwitcher />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center p-4 pt-20">
        <div className="w-full max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 transition-colors">
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="mb-6 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold flex items-center gap-2"
            >
              ← {t('common.back')}
            </button>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-4 dark:text-white">
                {t('datenschutz.title')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('datenschutz.lastUpdated')}: {new Date().toLocaleDateString()}
              </p>
            </div>

            {/* Content */}
            <div className="prose dark:prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.overview')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('datenschutz.overviewText')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.responsibleParty')}
                </h2>
                <div className="text-gray-700 dark:text-gray-300 space-y-1">
                  <p>{t('impressum.companyName')}</p>
                  <p>{t('impressum.street')}</p>
                  <p>{t('impressum.cityZip')}</p>
                  <p>{t('impressum.country')}</p>
                  <p className="mt-4">{t('impressum.email')}: info@example.com</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.dataCollection')}
                </h2>
                <h3 className="text-xl font-semibold mb-3 dark:text-white">
                  {t('datenschutz.cookies')}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.cookiesText')}
                </p>

                <h3 className="text-xl font-semibold mb-3 dark:text-white">
                  {t('datenschutz.serverLogs')}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.serverLogsText')}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                  <li>{t('datenschutz.browserType')}</li>
                  <li>{t('datenschutz.browserVersion')}</li>
                  <li>{t('datenschutz.operatingSystem')}</li>
                  <li>{t('datenschutz.referrerUrl')}</li>
                  <li>{t('datenschutz.hostname')}</li>
                  <li>{t('datenschutz.accessTime')}</li>
                  <li>{t('datenschutz.ipAddress')}</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.authentication')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.authText')}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                  <li>{t('datenschutz.authName')}</li>
                  <li>{t('datenschutz.authEmail')}</li>
                  <li>{t('datenschutz.authAvatar')}</li>
                  <li>{t('datenschutz.authUsername')}</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.yourRights')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.rightsIntro')}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                  <li><strong>{t('datenschutz.rightAccess')}</strong>: {t('datenschutz.rightAccessText')}</li>
                  <li><strong>{t('datenschutz.rightCorrection')}</strong>: {t('datenschutz.rightCorrectionText')}</li>
                  <li><strong>{t('datenschutz.rightDeletion')}</strong>: {t('datenschutz.rightDeletionText')}</li>
                  <li><strong>{t('datenschutz.rightRestriction')}</strong>: {t('datenschutz.rightRestrictionText')}</li>
                  <li><strong>{t('datenschutz.rightPortability')}</strong>: {t('datenschutz.rightPortabilityText')}</li>
                  <li><strong>{t('datenschutz.rightObjection')}</strong>: {t('datenschutz.rightObjectionText')}</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.dataSecurity')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('datenschutz.dataSecurityText')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.thirdParty')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.thirdPartyText')}
                </p>
                <h3 className="text-xl font-semibold mb-3 dark:text-white">Supabase</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('datenschutz.supabaseText')}
                </p>
                <h3 className="text-xl font-semibold mb-3 dark:text-white">Sentry</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('datenschutz.sentryText')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('datenschutz.contact')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('datenschutz.contactText')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Datenschutz
